import { SxProps, TableCell, TableRow, Theme } from "@mui/material";
import { CustomColumn, ITEM_HEIGHT } from "../..";
import RenderCell from "./RenderCell";

interface ColumnProps<T> {
  index: number;
  style: React.CSSProperties;
  data: {
    columns: string[];
    items: T;
    classes: {};
    stripeBgColor: string;
    pointerOnHover: boolean;
    mainColor: string;
    customColumns: CustomColumn;
    defaultColumnWidth: string | number;
    handleRowClick: (field: T, header: string) => void;
    applyCellStyling: (column: string, item: T) => SxProps;
    cellSx?: SxProps;
  };
}

export default function Column<T extends Record<string, any> = {}>({
  index,
  style,
  data,
}: ColumnProps<T>) {
  const {
    columns,
    items,
    stripeBgColor,
    pointerOnHover,
    mainColor,
    customColumns,
    defaultColumnWidth,
    handleRowClick,
    cellSx,
    applyCellStyling,
  } = data;
  const item = items[index];
  const STRIPE_OPACITY = 0.05;

  // Check if a value is numeric
  const isNumeric = (value: any, columnName: string): boolean => {
    // Handle null, undefined, empty string
    if (value === null || value === undefined || value === "") return false;

    // If explicitly declared as numeric in customColumns, respect that
    if (
      customColumns?.[columnName]?.type === "number" ||
      customColumns?.[columnName]?.isNumeric === true
    ) {
      return true;
    }

    // Check if string representation is numeric (including formatted numbers)
    if (typeof value === "string") {
      // If it starts with currency symbol, remove it for numeric check
      const currencySymbols = [
        "$",
        "€",
        "£",
        "¥",
        "₹",
        "₽",
        "₩",
        "S$",
        "C$",
        "A$",
        "HK$",
      ];
      let valueToCheck = value;

      for (const symbol of currencySymbols) {
        if (value.startsWith(symbol)) {
          valueToCheck = value.substring(symbol.length);
          break;
        }
      }

      // Remove commas and other formatting characters
      valueToCheck = valueToCheck.replace(/,/g, "");

      // Remove suffixes like '%' or 'days'
      valueToCheck = valueToCheck.replace(/\s*%$|\s*days$|\s*pts$/i, "");

      // Check if it's a numeric string
      return !isNaN(parseFloat(valueToCheck)) && isFinite(Number(valueToCheck));
    }

    // Check if it's a number
    return typeof value === "number";
  };

  return (
    <TableRow
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        boxSizing: "border-box",
        minWidth: "100%",
        width: "100%",
        backgroundColor:
          index % 2 === 0
            ? "white"
            : stripeBgColor || `rgba(0, 0, 0, ${STRIPE_OPACITY})`,
        transition: "background-color 0.2s",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        },
        borderBottom: "1px solid #f0f0f0",
        height: ITEM_HEIGHT,
      }}
      component="div"
      style={style}
    >
      {columns.map((column) => {
        const isDisabled = item?.disabled ?? false;
        const isNumberColumn =
          isNumeric(item[column], column) ||
          customColumns?.[column]?.type === "number" ||
          customColumns?.[column]?.isNumeric === true;

        // Determine if column is a Type or Status column for special styling
        const isTypeOrStatusColumn =
          column.toLowerCase().includes("type") ||
          column.toLowerCase().includes("status") ||
          column.toLowerCase() === "transaction_type" ||
          column.toLowerCase() === "status";

        // Create the cell styling as a proper SxProps object
        const cellStyling: SxProps<Theme> = {
          cursor: customColumns?.[column]?.disableClick
            ? "default"
            : pointerOnHover
              ? "pointer"
              : "none",
          color: isDisabled ? "grey" : mainColor,
          display: "flex",
          flexGrow: 1,
          flexShrink: 0,
          alignItems: "center",
          borderRight: "1px solid #f0f0f0",
          textAlign: "left",
          justifyContent: "flex-start",
          padding: isTypeOrStatusColumn ? "8px 16px" : "8px 12px",
          ...((cellSx as object) || {}),
          ...((applyCellStyling(column, item) as object) || {}),
        };

        const cellValue = item[column];
        const renderCell = customColumns[column]?.renderCell;

        return (
          <TableCell
            component="div"
            variant="body"
            key={`cellcolumn-${column}-${index}`}
            id={`cellcolumn-${column}-${index}`}
            sx={cellStyling}
            style={{
              flexBasis: "auto",
              height: ITEM_HEIGHT,
              width:
                customColumns?.[column]?.minWidth !== undefined
                  ? customColumns[column].minWidth
                  : defaultColumnWidth,
            }}
            onClick={() => {
              if (!customColumns?.[column]?.disableClick) {
                handleRowClick(item, column);
              }
            }}
          >
            {renderCell ? (
              renderCell({ value: cellValue, row: item })
            ) : (
              <RenderCell
                params={{ value: cellValue }}
                disabled={isDisabled}
                column={column}
                item={item}
                customColumns={customColumns}
                isNumeric={isNumberColumn}
              />
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
